import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Chip,
    Tabs,
    Tab,
    Stack,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { WeaponUpgradeFullDto } from "../../model/Dto/Dict/WeaponUpgrade";
import { useTraits } from "../../userHooks/useTraits";
import { WeaponTrait } from "../../model/Dto/Dict/Trait";
import { useUpgrades } from "../../userHooks/useUpgrades";
import { useWeapons } from "../../userHooks/useWeapons";
import { DictWeapon, CreateDictWeaponDto } from "../../model/Dto/Dict/Weapon";
import ConfirmDeleteDialog from "../../components/Dialog/ConfirmDeleteDialog";
import UpgradeDialog from "./Dialogs/UpgradeDialog";
import TraitDialog from "./Dialogs/TraitDialog";
import WeaponDialog from "./Dialogs/WeaponDialog";
import { DictWeaponTable } from "./DictWeaponTable";
import {CreateEquipmentCategoryDto, EquipmentCategory} from "../../model/Dto/Dict/EquipmentCategory";
import {useEquipmentCategories} from "../../userHooks/useEquipmentCategories";
import EquipmentCategoryDialog from "./Dialogs/EquipmentCategoryDialog";
import { FighterTypeDict, CreateFighterTypeDto, FIGHTER_TYPE_ENUM_LABELS } from "../../model/Dto/Dict/FighterType";
import { useFighterTypesDict } from "../../userHooks/useFighterTypesDict";
import FighterTypeDialog from "./Dialogs/FighterTypeDialog";
import { Api } from "../../request/api/api";
import { GetAllFactionsResult } from "../../request/api/faction/getAllFactions";
import { EquipmentDict, CreateEquipmentDto } from "../../model/Dto/Dict/Equipment";
import { useEquipmentDict } from "../../userHooks/useEquipmentDict";
import EquipmentDialog from "./Dialogs/EquipmentDialog";
import { XpSourceDict } from "../../model/Dto/Dict/XpSource";
import { useXpSourceDict } from "../../userHooks/useXpSourceDict";
import XpSourceDialog from "./Dialogs/XpSourceDialog";
import { SkillBranchDict, SaveSkillBranchDto } from "../../model/Dto/Dict/Skill";
import { useSkillBranches } from "../../userHooks/useSkillBranches";
import SkillBranchDialog from "./Dialogs/SkillBranchDialog";

const weaponTypeOptions = [
    { value: 1, label: "Basic" },
    { value: 2, label: "Pistol" },
    { value: 3, label: "Special" },
    { value: 4, label: "Heavy" },
    { value: 5, label: "CloseCombat" },
    { value: 6, label: "Grenade" },
    { value: 7, label: "AugmeticWeapons" },
    { value: 8, label: "Attachments" },
    { value: 9, label: "Innate" },
];

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dict-tabpanel-${index}`}
            aria-labelledby={`dict-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

export default function DictPage() {
    const [currentTab, setCurrentTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success",
    });

    // ---------- Trait ----------
    const { traits, loading: traitsLoading, createTrait, updateTrait, deleteTrait, error: traitsError } = useTraits();
    const [traitDialogOpen, setTraitDialogOpen] = useState(false);
    const [editingTrait, setEditingTrait] = useState<WeaponTrait | null>(null);
    const [traitToDelete, setTraitToDelete] = useState<WeaponTrait | null>(null);
    const [deleteTraitDialogOpen, setDeleteTraitDialogOpen] = useState(false);

    // ---------- Upgrade ----------
    const { upgrades, loading: upgradesLoading, createUpgrade, updateUpgrade, deleteUpgrade, error: upgradesError } = useUpgrades();
    const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
    const [editingUpgrade, setEditingUpgrade] = useState<WeaponUpgradeFullDto | null>(null);
    const [upgradeToDelete, setUpgradeToDelete] = useState<WeaponUpgradeFullDto | null>(null);
    const [deleteUpgradeDialogOpen, setDeleteUpgradeDialogOpen] = useState(false);

    // ---------- Weapon ----------
    const [selectedWeaponType, setSelectedWeaponType] = useState<number>(1);
    const { weapons, loading: weaponsLoading, createWeapon, updateWeapon, deleteWeapon, error: weaponsError } = useWeapons(selectedWeaponType);
    const [weaponDialogOpen, setWeaponDialogOpen] = useState(false);
    const [editingWeapon, setEditingWeapon] = useState<DictWeapon | null>(null);
    const [weaponToDelete, setWeaponToDelete] = useState<DictWeapon | null>(null);
    const [deleteWeaponDialogOpen, setDeleteWeaponDialogOpen] = useState(false);

    const { categories, loading: categoriesLoading, createCategory, updateCategory, deleteCategory, error: categoriesError } = useEquipmentCategories();
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<EquipmentCategory | null>(null);
    const [categoryToDelete, setCategoryToDelete] = useState<EquipmentCategory | null>(null);
    const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);

    // ---------- FighterType ----------
    const [fighterTypeFilter, setFighterTypeFilter] = useState("");
    const { fighterTypes, loading: fighterTypesLoading, createFighterType, updateFighterType, deleteFighterType, error: fighterTypesError } = useFighterTypesDict(fighterTypeFilter);
    const [factions, setFactions] = useState<GetAllFactionsResult>([]);
    const [fighterTypeDialogOpen, setFighterTypeDialogOpen] = useState(false);
    const [editingFighterType, setEditingFighterType] = useState<FighterTypeDict | null>(null);
    const [fighterTypeToDelete, setFighterTypeToDelete] = useState<FighterTypeDict | null>(null);
    const [deleteFighterTypeDialogOpen, setDeleteFighterTypeDialogOpen] = useState(false);

    // ---------- XpSource ----------
    const { xpSources, loading: xpSourcesLoading, createXpSource, updateXpSource, deleteXpSource, error: xpSourcesError } = useXpSourceDict();
    const [xpSourceDialogOpen, setXpSourceDialogOpen] = useState(false);
    const [editingXpSource, setEditingXpSource] = useState<XpSourceDict | null>(null);
    const [xpSourceToDelete, setXpSourceToDelete] = useState<XpSourceDict | null>(null);
    const [deleteXpSourceDialogOpen, setDeleteXpSourceDialogOpen] = useState(false);

    // ---------- SkillBranch ----------
    const { skillBranches, loading: skillBranchesLoading, createSkillBranch, updateSkillBranch, deleteSkillBranch, error: skillBranchesError } = useSkillBranches();
    const [skillBranchDialogOpen, setSkillBranchDialogOpen] = useState(false);
    const [editingSkillBranch, setEditingSkillBranch] = useState<SkillBranchDict | null>(null);
    const [skillBranchToDelete, setSkillBranchToDelete] = useState<SkillBranchDict | null>(null);
    const [deleteSkillBranchDialogOpen, setDeleteSkillBranchDialogOpen] = useState(false);

    // ---------- Equipment ----------
    const [equipmentCategoryFilter, setEquipmentCategoryFilter] = useState("");
    const { equipment, loading: equipmentLoading, createEquipment, updateEquipment, deleteEquipment, error: equipmentError } = useEquipmentDict(equipmentCategoryFilter);
    const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
    const [editingEquipment, setEditingEquipment] = useState<EquipmentDict | null>(null);
    const [equipmentToDelete, setEquipmentToDelete] = useState<EquipmentDict | null>(null);
    const [deleteEquipmentDialogOpen, setDeleteEquipmentDialogOpen] = useState(false);

    useEffect(() => {
        Api.factions.getAllFactions().then(setFactions);
    }, []);

    // ---------- Обработка ошибок ----------
    React.useEffect(() => {
        if (traitsError) setSnackbar({ open: true, message: traitsError, severity: "error" });
    }, [traitsError]);
    React.useEffect(() => {
        if (upgradesError) setSnackbar({ open: true, message: upgradesError, severity: "error" });
    }, [upgradesError]);
    React.useEffect(() => {
        if (weaponsError) setSnackbar({ open: true, message: weaponsError, severity: "error" });
    }, [weaponsError]);
    React.useEffect(() => {
        if (categoriesError) setSnackbar({ open: true, message: categoriesError, severity: "error" });
    }, [categoriesError]);
    React.useEffect(() => {
        if (fighterTypesError) setSnackbar({ open: true, message: fighterTypesError, severity: "error" });
    }, [fighterTypesError]);
    React.useEffect(() => {
        if (equipmentError) setSnackbar({ open: true, message: equipmentError, severity: "error" });
    }, [equipmentError]);
    React.useEffect(() => {
        if (xpSourcesError) setSnackbar({ open: true, message: xpSourcesError, severity: "error" });
    }, [xpSourcesError]);
    React.useEffect(() => {
        if (skillBranchesError) setSnackbar({ open: true, message: skillBranchesError, severity: "error" });
    }, [skillBranchesError]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
        setSearchQuery("");
    };

    // Фильтрация для Trait и Upgrade
    const filteredTraits = traits.filter(trait =>
        trait.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const filteredUpgrades = upgrades.filter(upgrade =>
        upgrade.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ---------- Trait handlers ----------
    const handleCreateTrait = async (data: { name: string; description: string }) => {
        const success = await createTrait(data);
        setSnackbar({ open: true, message: success ? "Свойство создано" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateTrait = async (data: { name: string; description: string }) => {
        if (!editingTrait) return false;
        const success = await updateTrait(editingTrait.id, data);
        if (success) setEditingTrait(null);
        setSnackbar({ open: true, message: success ? "Свойство обновлено" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteTrait = async () => {
        if (!traitToDelete) return;
        const success = await deleteTrait(traitToDelete.id);
        setDeleteTraitDialogOpen(false);
        if (success) setTraitToDelete(null);
        setSnackbar({ open: true, message: success ? "Свойство удалено" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- Upgrade handlers ----------
    const handleCreateUpgrade = async (data: any) => {
        const success = await createUpgrade(data);
        setSnackbar({ open: true, message: success ? "Апгрейд создан" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateUpgrade = async (data: any) => {
        if (!editingUpgrade) return false;
        const success = await updateUpgrade(editingUpgrade.id, data);
        if (success) setEditingUpgrade(null);
        setSnackbar({ open: true, message: success ? "Апгрейд обновлён" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteUpgrade = async () => {
        if (!upgradeToDelete) return;
        const success = await deleteUpgrade(upgradeToDelete.id);
        setDeleteUpgradeDialogOpen(false);
        if (success) setUpgradeToDelete(null);
        setSnackbar({ open: true, message: success ? "Апгрейд удалён" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- Weapon handlers ----------
    const handleCreateWeapon = async (data: CreateDictWeaponDto) => {
        const success = await createWeapon(data);
        setSnackbar({ open: true, message: success ? "Оружие создано" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateWeapon = async (data: CreateDictWeaponDto) => {
        if (!editingWeapon) return false;
        const success = await updateWeapon(editingWeapon.id, data);
        if (success) setEditingWeapon(null);
        setSnackbar({ open: true, message: success ? "Оружие обновлено" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };

    // Функция для фактического удаления после подтверждения
    const handleDeleteWeapon = async () => {
        if (!weaponToDelete) return;
        const success = await deleteWeapon(weaponToDelete.id);
        setDeleteWeaponDialogOpen(false);
        if (success) setWeaponToDelete(null);
        setSnackbar({ open: true, message: success ? "Оружие удалено" : "Ошибка", severity: success ? "success" : "error" });
    };

    // Открытие диалога подтверждения удаления
    const handleOpenDeleteWeaponDialog = (weapon: DictWeapon) => {
        setWeaponToDelete(weapon);
        setDeleteWeaponDialogOpen(true);
    };

    const handleEditWeapon = (weapon: DictWeapon) => {
        setEditingWeapon(weapon);
        setWeaponDialogOpen(true);
    };

    const handleCreateCategory = async (data: CreateEquipmentCategoryDto) => {
        const success = await createCategory(data);
        setSnackbar({ open: true, message: success ? "Категория создана" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateCategory = async (data: CreateEquipmentCategoryDto) => {
        if (!editingCategory) return false;
        const success = await updateCategory(editingCategory.id, data);
        if (success) setEditingCategory(null);
        setSnackbar({ open: true, message: success ? "Категория обновлена" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteCategory = async () => {
        if (!categoryToDelete) return;
        const success = await deleteCategory(categoryToDelete.id);
        setDeleteCategoryDialogOpen(false);
        if (success) setCategoryToDelete(null);
        setSnackbar({ open: true, message: success ? "Категория удалена" : "Ошибка", severity: success ? "success" : "error" });
    };
    const handleEditCategory = (cat: EquipmentCategory) => {
        setEditingCategory(cat);
        setCategoryDialogOpen(true);
    };
    const handleOpenDeleteCategoryDialog = (cat: EquipmentCategory) => {
        setCategoryToDelete(cat);
        setDeleteCategoryDialogOpen(true);
    };

    // ---------- FighterType handlers ----------
    const handleCreateFighterType = async (data: CreateFighterTypeDto) => {
        const success = await createFighterType(data);
        setSnackbar({ open: true, message: success ? "Тип бойца создан" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateFighterType = async (data: CreateFighterTypeDto) => {
        if (!editingFighterType) return false;
        const success = await updateFighterType(editingFighterType.id, data);
        if (success) setEditingFighterType(null);
        setSnackbar({ open: true, message: success ? "Тип бойца обновлён" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteFighterType = async () => {
        if (!fighterTypeToDelete) return;
        const success = await deleteFighterType(fighterTypeToDelete.id);
        setDeleteFighterTypeDialogOpen(false);
        if (success) setFighterTypeToDelete(null);
        setSnackbar({ open: true, message: success ? "Тип бойца удалён" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- XpSource handlers ----------
    const handleCreateXpSource = async (data: { name: string }) => {
        const success = await createXpSource(data);
        setSnackbar({ open: true, message: success ? "Источник создан" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateXpSource = async (data: { name: string }) => {
        if (!editingXpSource) return false;
        const success = await updateXpSource(editingXpSource.id, data);
        if (success) setEditingXpSource(null);
        setSnackbar({ open: true, message: success ? "Источник обновлён" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteXpSource = async () => {
        if (!xpSourceToDelete) return;
        const success = await deleteXpSource(xpSourceToDelete.id);
        setDeleteXpSourceDialogOpen(false);
        if (success) setXpSourceToDelete(null);
        setSnackbar({ open: true, message: success ? "Источник удалён" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- SkillBranch handlers ----------
    const handleCreateSkillBranch = async (data: SaveSkillBranchDto) => {
        const success = await createSkillBranch(data);
        setSnackbar({ open: true, message: success ? "Ветка навыков создана" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateSkillBranch = async (data: SaveSkillBranchDto) => {
        if (!editingSkillBranch) return false;
        const success = await updateSkillBranch(editingSkillBranch.id, data);
        if (success) setEditingSkillBranch(null);
        setSnackbar({ open: true, message: success ? "Ветка навыков обновлена" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteSkillBranch = async () => {
        if (!skillBranchToDelete) return;
        const success = await deleteSkillBranch(skillBranchToDelete.id);
        setDeleteSkillBranchDialogOpen(false);
        if (success) setSkillBranchToDelete(null);
        setSnackbar({ open: true, message: success ? "Ветка навыков удалена" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- Equipment handlers ----------
    const handleCreateEquipment = async (data: CreateEquipmentDto) => {
        const success = await createEquipment(data);
        setSnackbar({ open: true, message: success ? "Экипировка создана" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleUpdateEquipment = async (data: CreateEquipmentDto) => {
        if (!editingEquipment) return false;
        const success = await updateEquipment(editingEquipment.id, data);
        if (success) setEditingEquipment(null);
        setSnackbar({ open: true, message: success ? "Экипировка обновлена" : "Ошибка", severity: success ? "success" : "error" });
        return success;
    };
    const handleDeleteEquipment = async () => {
        if (!equipmentToDelete) return;
        const success = await deleteEquipment(equipmentToDelete.id);
        setDeleteEquipmentDialogOpen(false);
        if (success) setEquipmentToDelete(null);
        setSnackbar({ open: true, message: success ? "Экипировка удалена" : "Ошибка", severity: success ? "success" : "error" });
    };

    // ---------- Рендер таблиц ----------
    const renderTraitTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Описание</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {traitsLoading && traits.length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center">Загрузка...</TableCell></TableRow>
                    ) : filteredTraits.length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        filteredTraits.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell><Chip label={item.commonStatus === 1 ? "Активен" : "В архиве"} color={item.commonStatus === 1 ? "success" : "default"} size="small" /></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => { setEditingTrait(item); setTraitDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setTraitToDelete(item); setDeleteTraitDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderUpgradeTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Описание</TableCell>
                        <TableCell>Цена</TableCell>
                        <TableCell>Редкость</TableCell>
                        <TableCell>5-15 cr</TableCell>
                        <TableCell>20-45 cr</TableCell>
                        <TableCell>50-95 cr</TableCell>
                        <TableCell>100-120 cr</TableCell>
                        <TableCell>125+ cr</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {upgradesLoading && upgrades.length === 0 ? (
                        <TableRow><TableCell colSpan={11} align="center">Загрузка...</TableCell></TableRow>
                    ) : filteredUpgrades.length === 0 ? (
                        <TableRow><TableCell colSpan={11} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        filteredUpgrades.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>{item.price}</TableCell>
                                <TableCell>{item.rarity == null || item.rarity === 0 ? "common" : item.rarity}</TableCell>
                                <TableCell>{item.prices?.firstCategory ?? "-"}</TableCell>
                                <TableCell>{item.prices?.secondCategory ?? "-"}</TableCell>
                                <TableCell>{item.prices?.thirdCategory ?? "-"}</TableCell>
                                <TableCell>{item.prices?.fourthCategory ?? "-"}</TableCell>
                                <TableCell>{item.prices?.fifthCategory ?? "-"}</TableCell>
                                <TableCell><Chip label={item.commonStatus === 1 ? "Активен" : "В архиве"} color={item.commonStatus === 1 ? "success" : "default"} size="small" /></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => { setEditingUpgrade(item); setUpgradeDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setUpgradeToDelete(item); setDeleteUpgradeDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderCategoryTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Описание</TableCell>
                        <TableCell>Порядок</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {categoriesLoading && categories.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">Загрузка...</TableCell></TableRow>
                    ) : categories.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        categories.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>{item.sortNumber}</TableCell>
                                <TableCell><Chip label={item.commonStatus === 1 ? "Активен" : "В архиве"} color={item.commonStatus === 1 ? "success" : "default"} size="small" /></TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEditCategory(item)} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => handleOpenDeleteCategoryDialog(item)} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const fmtChar = (val: number, suffix: string) => `${val}${suffix}`;

    const renderFighterTypeTable = () => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Фракция</TableCell>
                        <TableCell align="center">Цена</TableCell>
                        <TableCell align="center">Ур.</TableCell>
                        <TableCell align="center">M</TableCell>
                        <TableCell align="center">WS</TableCell>
                        <TableCell align="center">BS</TableCell>
                        <TableCell align="center">S</TableCell>
                        <TableCell align="center">T</TableCell>
                        <TableCell align="center">W</TableCell>
                        <TableCell align="center">I</TableCell>
                        <TableCell align="center">A</TableCell>
                        <TableCell align="center">Ld</TableCell>
                        <TableCell align="center">Cl</TableCell>
                        <TableCell align="center">Wp</TableCell>
                        <TableCell align="center">Int</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fighterTypesLoading && fighterTypes.length === 0 ? (
                        <TableRow><TableCell colSpan={18} align="center">Загрузка...</TableCell></TableRow>
                    ) : fighterTypes.length === 0 ? (
                        <TableRow><TableCell colSpan={18} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        fighterTypes.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{FIGHTER_TYPE_ENUM_LABELS[item.fighterTypeEnum] ?? item.fighterTypeEnum}</TableCell>
                                <TableCell>{item.factionName ?? "Общедоступный"}</TableCell>
                                <TableCell align="center">{item.cost}</TableCell>
                                <TableCell align="center">{item.startingLevel}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.m, '"')}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.ws, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.bs, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.s, "")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.t, "")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.w, "")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.i, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.a, "")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.ld, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.cl, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.wp, "+")}</TableCell>
                                <TableCell align="center">{fmtChar(item.characteristics.int, "+")}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => { setEditingFighterType(item); setFighterTypeDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setFighterTypeToDelete(item); setDeleteFighterTypeDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderXpSourceTable = () => (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {xpSourcesLoading && xpSources.length === 0 ? (
                        <TableRow><TableCell colSpan={2} align="center">Загрузка...</TableCell></TableRow>
                    ) : xpSources.length === 0 ? (
                        <TableRow><TableCell colSpan={2} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        xpSources.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => { setEditingXpSource(item); setXpSourceDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setXpSourceToDelete(item); setDeleteXpSourceDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderSkillBranchTable = () => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width={60} align="center">Порядок</TableCell>
                        <TableCell>Ветка навыков</TableCell>
                        <TableCell>Навыки</TableCell>
                        <TableCell>Психосилы</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {skillBranchesLoading && skillBranches.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center">Загрузка...</TableCell></TableRow>
                    ) : skillBranches.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        skillBranches.map(branch => (
                            <TableRow key={branch.id} hover>
                                <TableCell align="center">{branch.order}</TableCell>
                                <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{branch.name}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {branch.skills.length === 0 ? (
                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                        ) : (
                                            branch.skills.map(skill => (
                                                <Chip key={skill.id} label={`${skill.order}. ${skill.name}`} size="small" variant="outlined" />
                                            ))
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {branch.isPsiPower && <Chip label="Да" color="secondary" size="small" />}
                                </TableCell>
                                <TableCell>
                                    <Chip label={branch.commonStatus === 1 ? "Активна" : "В архиве"} color={branch.commonStatus === 1 ? "success" : "default"} size="small" />
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                    <IconButton onClick={() => { setEditingSkillBranch(branch); setSkillBranchDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setSkillBranchToDelete(branch); setDeleteSkillBranchDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderEquipmentTable = () => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Название</TableCell>
                        <TableCell>Категория</TableCell>
                        <TableCell align="center">Цена</TableCell>
                        <TableCell align="center">Редкость</TableCell>
                        <TableCell align="right">Действия</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {equipmentLoading && equipment.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">Загрузка...</TableCell></TableRow>
                    ) : equipment.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center">Нет данных</TableCell></TableRow>
                    ) : (
                        equipment.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.categoryName}</TableCell>
                                <TableCell align="center">{item.cost}</TableCell>
                                <TableCell align="center">{item.rarity ?? "—"}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => { setEditingEquipment(item); setEquipmentDialogOpen(true); }} color="primary" size="small"><EditIcon /></IconButton>
                                    <IconButton onClick={() => { setEquipmentToDelete(item); setDeleteEquipmentDialogOpen(true); }} color="error" size="small"><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Справочники</Typography>
            <Paper sx={{ width: "100%" }}>
                <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    <Tab label="Свойства оружия" />
                    <Tab label="Фракции" disabled />
                    <Tab label="Типы бойцов" />
                    <Tab label="Навыки" />
                    <Tab label="Экипировка" />
                    <Tab label="Апгрейды оружия" />
                    <Tab label="Оружие" />
                    <Tab label="Категории экипировки" />
                    <Tab label="Источники опыта" />
                </Tabs>

                <TabPanel value={currentTab} index={0}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <TextField placeholder="Поиск по названию..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ width: 300 }} />
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingTrait(null); setTraitDialogOpen(true); }}>Добавить свойство</Button>
                        </Box>
                        {renderTraitTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={5}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <TextField placeholder="Поиск по названию..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ width: 300 }} />
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingUpgrade(null); setUpgradeDialogOpen(true); }}>Добавить апгрейд</Button>
                        </Box>
                        {renderUpgradeTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={6}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Тип оружия</InputLabel>
                                <Select value={selectedWeaponType} label="Тип оружия" onChange={(e) => setSelectedWeaponType(Number(e.target.value))}>
                                    {weaponTypeOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingWeapon(null); setWeaponDialogOpen(true); }}>Добавить оружие</Button>
                        </Box>
                        {weaponsLoading && weapons.length === 0 ? (
                            <Typography align="center">Загрузка...</Typography>
                        ) : (
                            <DictWeaponTable weapons={weapons} onEdit={handleEditWeapon} onDelete={handleOpenDeleteWeaponDialog} />
                        )}
                    </Stack>
                </TabPanel>

                {[1].map((idx) => (
                    <TabPanel key={idx} value={currentTab} index={idx}>
                        <Box sx={{ textAlign: "center", py: 8 }}><Typography variant="h6" color="text.secondary">Справочник в разработке</Typography></Box>
                    </TabPanel>
                ))}

                <TabPanel value={currentTab} index={3}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingSkillBranch(null); setSkillBranchDialogOpen(true); }}>Добавить ветку навыков</Button>
                        </Box>
                        {renderSkillBranchTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={2}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                            <FormControl sx={{ minWidth: 220 }}>
                                <InputLabel>Фракция</InputLabel>
                                <Select
                                    value={fighterTypeFilter}
                                    label="Фракция"
                                    onChange={(e) => setFighterTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="">Все</MenuItem>
                                    <MenuItem value="common">Общедоступные</MenuItem>
                                    {factions.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingFighterType(null); setFighterTypeDialogOpen(true); }}>Добавить тип</Button>
                        </Box>
                        {renderFighterTypeTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={4}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                            <FormControl sx={{ minWidth: 220 }}>
                                <InputLabel>Категория</InputLabel>
                                <Select
                                    value={equipmentCategoryFilter}
                                    label="Категория"
                                    onChange={(e) => setEquipmentCategoryFilter(e.target.value)}
                                >
                                    <MenuItem value="">Все</MenuItem>
                                    {categories.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingEquipment(null); setEquipmentDialogOpen(true); }}>Добавить экипировку</Button>
                        </Box>
                        {renderEquipmentTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={7}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <TextField placeholder="Поиск по названию..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }} sx={{ width: 300 }} />
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingCategory(null); setCategoryDialogOpen(true); }}>Добавить категорию</Button>
                        </Box>
                        {renderCategoryTable()}
                    </Stack>
                </TabPanel>

                <TabPanel value={currentTab} index={8}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingXpSource(null); setXpSourceDialogOpen(true); }}>Добавить источник</Button>
                        </Box>
                        {renderXpSourceTable()}
                    </Stack>
                </TabPanel>
            </Paper>

            <TraitDialog open={traitDialogOpen} onClose={() => { setTraitDialogOpen(false); setEditingTrait(null); }} onSave={editingTrait ? handleUpdateTrait : handleCreateTrait} initialData={editingTrait} title={editingTrait ? "Редактировать свойство" : "Добавить свойство"} />
            <ConfirmDeleteDialog open={deleteTraitDialogOpen} onClose={() => { setDeleteTraitDialogOpen(false); setTraitToDelete(null); }} onConfirm={handleDeleteTrait} itemName={traitToDelete?.name || ""} loading={traitsLoading} />

            <UpgradeDialog open={upgradeDialogOpen} onClose={() => { setUpgradeDialogOpen(false); setEditingUpgrade(null); }} onSave={editingUpgrade ? handleUpdateUpgrade : handleCreateUpgrade} initialData={editingUpgrade} title={editingUpgrade ? "Редактировать апгрейд" : "Добавить апгрейд"} />
            <ConfirmDeleteDialog open={deleteUpgradeDialogOpen} onClose={() => { setDeleteUpgradeDialogOpen(false); setUpgradeToDelete(null); }} onConfirm={handleDeleteUpgrade} itemName={upgradeToDelete?.name || ""} loading={upgradesLoading} />

            <WeaponDialog open={weaponDialogOpen} onClose={() => { setWeaponDialogOpen(false); setEditingWeapon(null); }} onSave={editingWeapon ? handleUpdateWeapon : handleCreateWeapon} initialData={editingWeapon} />
            <ConfirmDeleteDialog open={deleteWeaponDialogOpen} onClose={() => { setDeleteWeaponDialogOpen(false); setWeaponToDelete(null); }} onConfirm={handleDeleteWeapon} itemName={weaponToDelete?.name || ""} loading={weaponsLoading} />

            <EquipmentCategoryDialog
                open={categoryDialogOpen}
                onClose={() => { setCategoryDialogOpen(false); setEditingCategory(null); }}
                onSave={editingCategory ? handleUpdateCategory : handleCreateCategory}
                initialData={editingCategory}
                title={editingCategory ? "Редактировать категорию" : "Добавить категорию"}
            />
            <ConfirmDeleteDialog
                open={deleteCategoryDialogOpen}
                onClose={() => { setDeleteCategoryDialogOpen(false); setCategoryToDelete(null); }}
                onConfirm={handleDeleteCategory}
                itemName={categoryToDelete?.name || ""}
                loading={categoriesLoading}
            />

            <FighterTypeDialog
                open={fighterTypeDialogOpen}
                onClose={() => { setFighterTypeDialogOpen(false); setEditingFighterType(null); }}
                onSave={editingFighterType ? handleUpdateFighterType : handleCreateFighterType}
                initialData={editingFighterType}
                title={editingFighterType ? "Редактировать тип бойца" : "Добавить тип бойца"}
                factions={factions}
            />
            <ConfirmDeleteDialog
                open={deleteFighterTypeDialogOpen}
                onClose={() => { setDeleteFighterTypeDialogOpen(false); setFighterTypeToDelete(null); }}
                onConfirm={handleDeleteFighterType}
                itemName={fighterTypeToDelete?.name ?? ""}
                loading={fighterTypesLoading}
            />

            <EquipmentDialog
                open={equipmentDialogOpen}
                onClose={() => { setEquipmentDialogOpen(false); setEditingEquipment(null); }}
                onSave={editingEquipment ? handleUpdateEquipment : handleCreateEquipment}
                initialData={editingEquipment}
                title={editingEquipment ? "Редактировать экипировку" : "Добавить экипировку"}
                categories={categories}
            />
            <ConfirmDeleteDialog
                open={deleteEquipmentDialogOpen}
                onClose={() => { setDeleteEquipmentDialogOpen(false); setEquipmentToDelete(null); }}
                onConfirm={handleDeleteEquipment}
                itemName={equipmentToDelete?.name ?? ""}
                loading={equipmentLoading}
            />

            <XpSourceDialog
                open={xpSourceDialogOpen}
                onClose={() => { setXpSourceDialogOpen(false); setEditingXpSource(null); }}
                onSave={editingXpSource ? handleUpdateXpSource : handleCreateXpSource}
                initialData={editingXpSource}
                title={editingXpSource ? "Редактировать источник опыта" : "Добавить источник опыта"}
            />
            <ConfirmDeleteDialog
                open={deleteXpSourceDialogOpen}
                onClose={() => { setDeleteXpSourceDialogOpen(false); setXpSourceToDelete(null); }}
                onConfirm={handleDeleteXpSource}
                itemName={xpSourceToDelete?.name ?? ""}
                loading={xpSourcesLoading}
            />

            <SkillBranchDialog
                open={skillBranchDialogOpen}
                onClose={() => { setSkillBranchDialogOpen(false); setEditingSkillBranch(null); }}
                onSave={editingSkillBranch ? handleUpdateSkillBranch : handleCreateSkillBranch}
                initialData={editingSkillBranch}
                title={editingSkillBranch ? "Редактировать ветку навыков" : "Добавить ветку навыков"}
            />
            <ConfirmDeleteDialog
                open={deleteSkillBranchDialogOpen}
                onClose={() => { setDeleteSkillBranchDialogOpen(false); setSkillBranchToDelete(null); }}
                onConfirm={handleDeleteSkillBranch}
                itemName={skillBranchToDelete?.name ?? ""}
                loading={skillBranchesLoading}
            />

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}